/**
 * Google Analytics consent (DWEB-15).
 *
 * SCOPE, deliberately narrow. Google Analytics is the ONLY consent-requiring
 * storage on this site. `document.cookie` appears nowhere in src/, there are no
 * first-party cookies, and localStorage holds only the visitor's own UI state
 * (theme; since v6.5.2 also whether they dismissed the chat bubble,
 * DynaMiteWidget.js, DWEB-142) plus this flag — functional, not tracking.
 *
 * DWEB-142: the chat bubble beside the DynaMite launcher must not appear
 * until this choice is made, so a decision is ANNOUNCED on the window as
 * CONSENT_CHANGED_EVENT the moment it is stored. The `storage` event would
 * not do — it fires in OTHER tabs only. Same-tab listeners subscribe to this.
 *
 * THIS IS NOT THE AI CONSENT. The DynaMite chat has its own, separate,
 * pre-existing dialog (Chatbot.js). The two must never be merged: conflating
 * them would imply the model sends data off-device, which is the opposite of
 * the site's central claim. This module knows nothing about the model.
 */

/** localStorage, not sessionStorage: the choice has to survive a reload AND a return visit. */
export const CONSENT_KEY = 'analyticsConsent';

export const GRANTED = 'granted';
export const DENIED = 'denied';

/** Dispatched on `window` after a choice is stored; `detail` is the value. */
export const CONSENT_CHANGED_EVENT = 'analyticsConsentChanged';

/** The stored choice, or null when the visitor has not answered yet. */
export function getStoredConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    return raw === GRANTED || raw === DENIED ? raw : null;
  } catch {
    // Private mode or a blocked store: treat as unanswered, never as granted.
    return null;
  }
}

function gtag(...args) {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(args);
}

/** Has the gtag.js tag already been injected? */
function tagAlreadyLoaded() {
  return !!document.querySelector('script[data-ga-tag="1"]');
}

/**
 * Inject gtag.js. Called ONLY after an explicit grant — this is the request
 * that must not exist before consent.
 */
function loadGoogleAnalytics() {
  const id = window.__GA_MEASUREMENT_ID__;
  // CRA leaves the %PLACEHOLDER% verbatim when the env var is unset, so a build
  // without an id must not inject a script pointing at a literal placeholder.
  if (!id || id.startsWith('%') || tagAlreadyLoaded()) return;

  const script = document.createElement('script');
  script.async = true;
  script.dataset.gaTag = '1';
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(script);
  gtag('config', id);
}

/** Record and apply a choice. `granted` loads the tag; `denied` never does. */
export function setConsent(choice) {
  const value = choice === GRANTED ? GRANTED : DENIED;
  try {
    localStorage.setItem(CONSENT_KEY, value);
  } catch {
    // Storage unavailable — honour the choice for this page view regardless.
  }
  gtag('consent', 'update', { analytics_storage: value });
  if (value === GRANTED) loadGoogleAnalytics();
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT, { detail: value }));
  }
  return value;
}

/**
 * Replay a previously stored grant on a fresh page load.
 *
 * Denial is a no-op by construction: the default in index.html is already
 * denied and the tag is never injected, so there is nothing to undo.
 */
export function applyStoredConsent() {
  if (getStoredConsent() === GRANTED) {
    gtag('consent', 'update', { analytics_storage: GRANTED });
    loadGoogleAnalytics();
    return GRANTED;
  }
  return getStoredConsent();
}
